import React, { useState, useEffect } from 'react';
import { Course, Instructor } from '../types';
import CourseCard from '../components/CourseCard';
import TrackBadge from '../components/TrackBadge';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTracks } from '../hooks/useTracks';

const CourseCatalog: React.FC = () => {
  usePageTitle('Course Catalog');
  const trackList = useTracks();
  const tracks = ['All Tracks', ...trackList.map((t) => t.name)];
  const [courses, setCourses] = useState<Course[]>([]);
  const [instructors, setInstructors] = useState<Record<string, Instructor>>({});
  const [selectedTrack, setSelectedTrack] = useState('All Tracks');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, instructorsRes] = await Promise.all([
          fetch('/api/courses'),
          fetch('/api/instructors'),
        ]);

        if (coursesRes.ok) {
          const data: Course[] = await coursesRes.json();
          setCourses(data.filter((c) => c.active));
        }

        if (instructorsRes.ok) {
          const data: Instructor[] = await instructorsRes.json();
          const map: Record<string, Instructor> = {};
          data.forEach((i) => (map[i.id] = i));
          setInstructors(map);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredCourses = courses.filter((course) => {
    const trackMatch = selectedTrack === 'All Tracks' || course.track === selectedTrack;
    const searchMatch =
      searchQuery === '' ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.shortDescription.toLowerCase().includes(searchQuery.toLowerCase());
    return trackMatch && searchMatch;
  });

  // Group by known tracks, then append any course tracks not in the managed
  // list so a course never disappears from the "All Tracks" view.
  const knownTracks = tracks.slice(1);
  const orphanTracks = Array.from(
    new Set(filteredCourses.map((c) => c.track).filter((t) => t && !knownTracks.includes(t)))
  );
  const groupedCourses = [...knownTracks, ...orphanTracks].reduce<Record<string, Course[]>>((acc, track) => {
    acc[track] = filteredCourses.filter((c) => c.track === track);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading courses...</p>
      </div>
    );
  }

  return (
    <div className="catalog-page">
      <section className="catalog-header">
        <div className="section-container">
          <h1 className="page-title">Course Catalog</h1>
          <p className="page-subtitle">
            Browse our comprehensive Microsoft Security training catalog.
            All courses are instructor-led with hands-on labs.
          </p>
        </div>
      </section>

      <section className="catalog-filters">
        <div className="section-container">
          <div className="filters-bar">
            <div className="filter-group">
              <label className="filter-label">Track</label>
              <div className="filter-buttons">
                {tracks.map((track) => (
                  <button
                    key={track}
                    className={`filter-btn ${selectedTrack === track ? 'filter-active' : ''}`}
                    onClick={() => setSelectedTrack(track)}
                  >
                    {track}
                  </button>
                ))}
              </div>
            </div>
            <div className="filter-group filter-search">
              <label className="filter-label">Search</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <p className="filter-results">{filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} found</p>
        </div>
      </section>

      <section className="catalog-courses">
        <div className="section-container">
          {selectedTrack === 'All Tracks' ? (
            Object.entries(groupedCourses).map(([track, trackCourses]) =>
              trackCourses.length > 0 ? (
                <div key={track} className="track-group">
                  <div className="track-group-header">
                    <TrackBadge track={track} size="large" />
                    <span className="track-count">{trackCourses.length} course{trackCourses.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="courses-grid">
                    {trackCourses.map((course) => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        instructor={instructors[course.instructorId]}
                      />
                    ))}
                  </div>
                </div>
              ) : null
            )
          ) : (
            <div className="courses-grid">
              {filteredCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  instructor={instructors[course.instructorId]}
                />
              ))}
            </div>
          )}

          {filteredCourses.length === 0 && (
            <div className="empty-state">
              <p>No courses match your filters. Try adjusting your search criteria.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CourseCatalog;
